
import { map, mergeMap, filter } from 'rxjs/operators'
import { Epic, EpicObservable } from 'interfaces/middleware'
import { combineEpics } from 'redux-observable'
import {
  GET_SOCIAL_INSIGHTS,
  getSocialInsightsActionCreator,
  TOGGLE_SOCIAL_INSIGHT_LIKE,
  toggleSocialInsightLikeActionCreator,
  CLOSE_REVIEW_COMPOSE_PANE,
} from 'actions/socialInsightsActions'
import { get, post } from 'helpers/observableAjaxHelpers'
import { GET_SOCIAL_INSIGHTS_URL, LIKE_POST_URL, UNLIKE_POST_URL } from 'interfaces/ajax'
import { merge, of, empty } from 'rxjs'
import { SOCIAL_INSIGHT_PLACEMENTS_BY_PAGE } from 'constants/insights'
import { normalize } from 'helpers/normalizeHelpers'
import { compose } from 'redux'
import { convertCTAFilterCategories, filterBadInsights, filterForbiddenCTAs } from 'helpers/insightHelpers'
import { InsightId } from 'interfaces/insights'
import { SocialInsight, SocialInnerInsight, GetSocialInsightsApiPayload } from 'interfaces/socialInsights'
import { Action } from 'actions'
import { COMPOSE_SUBMIT } from 'actions/feedComposeActions'
import { modifyFloatingPaneActionCreator, killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { FloatingPane, OpportunityReviewFloatingPane } from 'interfaces/floatingPanes'
import { POST_TYPE_SOCIAL_INSIGHT, FEED_ORDERING_BY_POPULARITY } from 'interfaces/feed'
import { createFeedAllCommentUidsByPostUidSelector } from 'selectors/feedSelectors'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { ajaxCatch } from 'rxOperators/ajaxCatch'

type ReduceAcc = {
  insights: SocialInnerInsight[],
  socialInsights: Record<InsightId, SocialInsight>,
}

const getSocialInsightsEpic: Epic = (action$) => action$
  .pipe(filterRequestOfType(GET_SOCIAL_INSIGHTS, 'request')).pipe(
    mergeMap((action) => {
      const { page } = action.payload.params

      const placements = SOCIAL_INSIGHT_PLACEMENTS_BY_PAGE[page]
      const apiPayload: GetSocialInsightsApiPayload = {
        placement: placements,
      }
      const request$ = get(GET_SOCIAL_INSIGHTS_URL, apiPayload)

      return request$.pipe(
        mergeMap(({ response }) => {
          const { insights, socialInsights: socialInsightsMap } = response.items.reduce<ReduceAcc>(
            ({ insights: prevInsights, socialInsights: prevSocialInsights }, item) => {
              const { insight, is_liked, ...restSocialInsight } = item
              const nextSocialInsight: SocialInsight = {
                ...restSocialInsight,
                insightId: insight.id,
                placement: insight.placement,
                comments: null,
                commentsOrdering: FEED_ORDERING_BY_POPULARITY,
                type: POST_TYPE_SOCIAL_INSIGHT,
                is_liked: !!is_liked,
              }

              const nextInsight: SocialInnerInsight = {
                ...item.insight,
                socialInsightUid: nextSocialInsight.uid,
              }

              return {
                insights: [
                  ...prevInsights,
                  nextInsight,
                ],
                socialInsights: {
                  ...prevSocialInsights,
                  [insight.id]: nextSocialInsight,
                },
              }
            },
            {
              insights: [],
              socialInsights: {},
            }
          )

          const handledInsights = compose<
          SocialInnerInsight[],
          SocialInnerInsight[],
          SocialInnerInsight[],
          SocialInnerInsight[]
        >(
          convertCTAFilterCategories,
          filterBadInsights,
          filterForbiddenCTAs,
        )(insights)

          const socialInsights = handledInsights.map((insight) => socialInsightsMap[insight.id])
          const insightsCollection = normalize(handledInsights, 'id')
          return of<Action>(
            getSocialInsightsActionCreator.success(action.payload.params, {
              insights: insightsCollection,
              socialInsights: normalize(socialInsights, 'uid'),
              placements,
            })
          )
        }))
        .pipe(ajaxCatch(() => {
          return of(getSocialInsightsActionCreator.fail(action.payload.params))
        }))
    }))

const toggleSocialInsightLikeEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_SOCIAL_INSIGHT_LIKE, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { socialInsightUid: uid, toLike } = params

        const request$ = toLike
          ? post({ url: LIKE_POST_URL, params: { uid }})
          : post({ url: UNLIKE_POST_URL, params: { uid }})

        return request$.pipe(
          map(({ response }) =>
            toggleSocialInsightLikeActionCreator.success(params, response)
          ))
          .pipe(ajaxCatch((error) =>
            of(toggleSocialInsightLikeActionCreator.fail(params, error.status))
          ))
      }))

const TYPED_PANE: Pick<FloatingPane, 'type'> = {
  type: 'opportunity-review',
}

const paneSelector = createFloatingPaneByTypeSelector()
const commentUidsSelector = createFeedAllCommentUidsByPostUidSelector()

const createReviewComposePaneAction$ = (action$: EpicObservable) =>
  merge(
    action$
      .pipe(filterRequestOfType(COMPOSE_SUBMIT, 'success')).pipe(
        filter((action) => !!action.payload.response.isOpportunityReview)),
    action$
      .pipe(byType(CLOSE_REVIEW_COMPOSE_PANE))
  )

const successSubmitEpic: Epic = (action$, store) =>
  createReviewComposePaneAction$(action$).pipe(
    mergeMap(() => {
      const state = store.getState()
      const pane = paneSelector(state, TYPED_PANE) as OpportunityReviewFloatingPane | null
      if (!pane) {
        return empty()
      }

      const { commentUid, uid: socialInsightUid, ...restPayload } = pane.payload

      const commentUids = commentUidsSelector(state, { postUid: socialInsightUid })
      // when there is a member's own review we should close the pane entirely
      if (!commentUids || commentUids.results.length <= 1) {
        return of<Action>(killFloatingPanesActionCreator('opportunity-review'))
      }

      const action = modifyFloatingPaneActionCreator<OpportunityReviewFloatingPane>('opportunity-review', {
        type: 'opportunity-review',
        payload: {
          ...restPayload,
          uid: socialInsightUid,
          mode: 'comments',
        },
      }) as Action

      return of(action)
    }))

export const socialInsightsEpic = combineEpics(
  getSocialInsightsEpic,
  toggleSocialInsightLikeEpic,
  successSubmitEpic,
)
